/**
 * @fileoverview Browser-side helper that bundles asset files into a single
 * `.zip` Blob using JSZip. Used by the project download split button.
 *
 * Hidden behind the `projectDownload` feature flag at the call sites.
 */

import JSZip from "jszip";

/** A single file to include in the zip. */
export interface AssetZipEntry {
  /** Filename inside the zip (no path prefix). */
  filename: string;
  /** File contents — Blob, ArrayBuffer, or string. */
  data: Blob | ArrayBuffer | string;
}

/**
 * Builds a deflate-compressed zip from the given entries.
 *
 * @param entries - The files to include. Order is preserved.
 * @returns A `Blob` of MIME `application/zip` ready for `triggerBrowserDownload`.
 * @throws If `entries` is empty (can't make an empty zip — likely a bug).
 */
export async function buildAssetZip(
  entries: readonly AssetZipEntry[],
): Promise<Blob> {
  if (entries.length === 0) {
    throw new Error("buildAssetZip requires at least one entry");
  }
  const zip = new JSZip();
  for (const entry of entries) {
    zip.file(entry.filename, entry.data);
  }
  return zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
}
